import { Dialog } from '@base-ui/react/dialog';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { wrapTab } from '~/shared/lib/wrapTab';
import { CloseGlyph } from '~/shared/ui/FormGlyphs';

import styles from './ClipPlayer.module.css';

/** The canon's two columns, YouTube's keys on the left and the player's own on the right. */
const COLUMNS = [
  [
    ['playPause', 'keys.space'],
    ['tenSeconds', 'J / L'],
    ['fiveSeconds', 'keys.arrows'],
    ['frame', ', / .'],
    ['speed', '< / >'],
    ['percent', '0–9'],
    ['ends', 'keys.ends'],
    ['volume', 'keys.volume'],
    ['mute', 'M'],
  ],
  [
    ['theatre', 'T'],
    ['fullscreen', 'F'],
    ['clips', 'keys.clips'],
    ['comments', 'keys.comments'],
    ['comment', 'C'],
    ['loop', '[ / ] / \\'],
    ['compare', 'S'],
    ['zoom', 'Z'],
    ['pan', 'keys.pan'],
    ['unread', 'keys.unread'],
  ],
] as const;

interface ShortcutsSheetProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The player itself, focused here: Base UI would return focus to its first tabbable instead. */
  readonly finalFocus: RefObject<HTMLElement | null>;
}

export function ShortcutsSheet({ open, onOpenChange, finalFocus }: ShortcutsSheetProps) {
  const { t } = useTranslation();
  const keyOf = (key: string) => (key.startsWith('keys.') ? t(`web:clipPlayer.sheet.${key}`) : key);
  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        {/* A nested dialog draws no backdrop unless told to; the canon dims the player behind. */}
        <Dialog.Backdrop className={styles.sheetBackdrop} forceRender />
        <Dialog.Popup
          className={styles.sheet}
          finalFocus={() => {
            finalFocus.current?.focus();
            return false;
          }}
          onKeyDown={(event) => {
            wrapTab(event, event.currentTarget);
          }}
        >
          <div className={styles.sheetHead}>
            <Dialog.Title className={styles.sheetTitle}>{t('web:clipPlayer.sheet.title')}</Dialog.Title>
            <Dialog.Description className={styles.sheetSub}>
              {t('web:clipPlayer.sheet.sub')}
            </Dialog.Description>
            <Dialog.Close aria-label={t('web:clipPlayer.sheet.close')} className={styles.icon}>
              <CloseGlyph className={styles.glyph} />
            </Dialog.Close>
          </div>
          <div className={styles.sheetGrid}>
            {COLUMNS.map((column, index) => (
              <dl className={styles.sheetColumn} key={index}>
                {column.map(([label, key]) => (
                  <div className={styles.sheetRow} key={label}>
                    <dt>{t(`web:clipPlayer.sheet.${label}`)}</dt>
                    <dd>{keyOf(key)}</dd>
                  </div>
                ))}
              </dl>
            ))}
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
